import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { fmtDateTime } from "@/components/EntityHistory";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const ALL = "tutti";

const Filters = ({ f, set, data }) => (
  <div className="mb-5 flex flex-wrap items-end gap-3">
    <Select value={f.user_id} onValueChange={(v) => set({ user_id: v })}>
      <SelectTrigger className="w-48" data-testid="activity-filter-user"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>Tutti gli utenti</SelectItem>
        {(data?.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
      </SelectContent>
    </Select>
    <Select value={f.type} onValueChange={(v) => set({ type: v })}>
      <SelectTrigger className="w-48" data-testid="activity-filter-type"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>Tutte le modifiche</SelectItem>
        {Object.entries(data?.types || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
    <label className="text-xs text-muted-foreground">Dal<Input type="date" value={f.date_from} onChange={(e) => set({ date_from: e.target.value })} className="mt-1 w-40" data-testid="activity-filter-from" /></label>
    <label className="text-xs text-muted-foreground">Al<Input type="date" value={f.date_to} onChange={(e) => set({ date_to: e.target.value })} className="mt-1 w-40" data-testid="activity-filter-to" /></label>
  </div>
);

export default function Attivita() {
  const [f, setF] = useState({ user_id: ALL, type: ALL, date_from: "", date_to: "", page: 1 });
  const set = (x) => setF({ ...f, ...x, page: x.page || 1 });
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== ALL));
  const { data, isLoading } = useQuery({ queryKey: ["activity", params], queryFn: async () => (await api.get("/activity", { params })).data, placeholderData: keepPreviousData });
  return (
    <div data-testid="attivita-page">
      <PageHeader title="Registro attività" subtitle="Chi ha modificato misure clienti, quantità prodotte e tabelle misure, e quando" />
      <Filters f={f} set={set} data={data} />
      <Card className="divide-y divide-border/50">
        {isLoading ? <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          : !data?.items.length ? <p className="p-10 text-center text-sm text-muted-foreground" data-testid="activity-empty">Nessuna attività registrata</p>
          : data.items.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-testid="activity-row">
              <div className="min-w-0">
                <p><span className="font-medium" data-testid="activity-user">{a.user_name}</span> <span className="text-muted-foreground">ha modificato</span> {a.label}</p>
                <Badge variant="outline" className="mt-1 text-[10px]">{data.types[a.type]}</Badge>
              </div>
              <span className="font-mono-erp text-xs text-muted-foreground" data-testid="activity-date">{fmtDateTime(a.at)}</span>
            </div>
          ))}
      </Card>
      {data?.pages > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2 text-sm">
          <Button variant="outline" size="icon" disabled={f.page <= 1} onClick={() => set({ page: f.page - 1 })} data-testid="activity-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span data-testid="activity-page">{f.page} / {data.pages}</span>
          <Button variant="outline" size="icon" disabled={f.page >= data.pages} onClick={() => set({ page: f.page + 1 })} data-testid="activity-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}
